import {
  LookupIcon,
  MapAreaIcon,
  ProfilingIcon,
  SpinnerIcon,
  StorageIcon
} from "@/assets/icons";
import { Button } from "@/components/ui/button";
import Divider from "@/components/ui/Divider";
import TimeAgo from "@/components/ui/TimeAgo";
import { MESSAGE } from "@/constants/message";
import { getEstimatedTimeFromSeconds } from "@/helpers/time";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import {
  AnalysisJobAreaInputType,
  AnalysisJobItem,
  AnalysisJobStatus
} from "@/types/analysis";
import { AnalysisResultType } from "@/types/dataset";
import { GetUserProfileData } from "@/types/userProfile";

import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

interface JobItemProps {
  data: AnalysisJobItem;
  unOpenedJobIds?: string[];
  onOpen?: (id: string) => void;
  onCancel?: (id: string) => void;
  onRerun?: (id: string) => void;
  userProfileData?: GetUserProfileData;
}

export default function JobItem({
  data,
  unOpenedJobIds,
  onCancel,
  onOpen,
  onRerun,
  userProfileData
}: Readonly<JobItemProps>) {
  const { showConfirmation } = useConfirmationModal();

  const { isRunning, isWaiting, isComplete } = {
    isRunning: data.status === AnalysisJobStatus.IN_PROGRESS,
    isWaiting: data.status === AnalysisJobStatus.QUEUED,
    isComplete: data.status === AnalysisJobStatus.SUCCESS
  };

  const handleOnOpen = () => {
    if (isComplete) {
      const expired = dayjs(data.expired_at).isBefore(dayjs());
      if (expired) {
        showConfirmation({
          title: "Data Analysis Expired",
          message: MESSAGE.ANALYSIS_EXPIRED,
          confirmLabel: "Re-Run",
          cancelLabel: "Later",
          onCancel: () => {},
          onConfirm: () => {
            onRerun?.(data.id);
          }
        });
        return;
      }
      onOpen?.(data.id);
    }
  };

  const handleOnCancel = () => {
    const msg = isRunning
      ? MESSAGE.CANCEL_INPROGRESS_ANALYSIS_CONFIRMATION
      : MESSAGE.CANCEL_INWAITING_ANALYSIS_CONFIRMATION;

    const confirmLabel = isRunning ? "Keep Running" : "Keep in Queue";
    showConfirmation({
      title: "Cancel Analysis?",
      message: msg,
      cancelLabel: "Cancel Analysis",
      confirmLabel,
      onConfirm: () => {},
      onCancel: () => {
        onCancel?.(data.id);
      }
    });
  };

  const getBtnConfig = () => {
    if (isRunning || isWaiting) {
      return {
        label: "Cancel",
        action: handleOnCancel
      };
    }

    if (isComplete) {
      return {
        label: "Open",
        action: handleOnOpen
      };
    }

    return {
      label: "",
      action: () => {}
    };
  };

  const { label, action } = getBtnConfig();

  const getType = () => {
    if (!data.input_type) return "-";
    return data.input_type === AnalysisJobAreaInputType.ADMIN
      ? "Adm Area"
      : "Catchment";
  };

  const isProfiling =
    data.output_type === AnalysisResultType.TYPE_SITE_PROFILING;

  const getResultText = () => {
    if (!data.output_type) return "-";
    return isProfiling ? "Profiling" : "Grid";
  };

  const shouldHideButton =
    (isRunning || isWaiting) &&
    userProfileData?.full_name.toLowerCase() !== data.created_by.toLowerCase();

  const estimatedTime = data?.estimated_time_secs;

  return (
    <div className="relative w-full text-left rounded-lg h-auto border border-gainsboro bg-white p-2 hover:bg-teal-50 hover:border-teal-200 group">
      <div className="py-2 px-1 overflow-hidden hover:cursor-pointer">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <p className="text-[10px] text-txtPlaceholder font-normal">
              <TimeAgo date={data.created_at} />
              {data.created_by && <span> - by {data.created_by}</span>}{" "}
              {isRunning && (
                <span>
                  {estimatedTime && estimatedTime > 0
                    ? `(est in ${getEstimatedTimeFromSeconds(estimatedTime)})`
                    : `(estimating...)`}
                </span>
              )}
            </p>
            <p
              className={twMerge(
                "text-txtHeading font-bold line-clamp-2 group-hover:text-teal-500"
              )}
              title={"name"}
            >
              {data.name || `Spatial Analysis (${data.id})`}
            </p>
            {isComplete && unOpenedJobIds?.includes(data.id) && (
              <div className="absolute top-10 -right-[2px] h-2 w-2 bg-red-500 rounded-full" />
            )}
          </div>
          {isRunning && (
            <SpinnerIcon data-testid="spinner-icon" className="animate-spin" />
          )}
        </div>
        <Divider className="bg-white-smoke w-full h-[1px] my-2" />
        <div className="flex">
          <div className="w-1/3 flex items-center gap-2">
            <StorageIcon width={16} className="fill-purple-500" />
            <span className="text-[10px]">
              {`${data.datasets?.length || 0} dataset${data.datasets?.length <= 1 ? "" : "s"}`}
            </span>
          </div>
          <div className="w-1/3 flex items-center gap-2">
            <MapAreaIcon width={16} />
            <span className="text-[10px]">{getType()}</span>
          </div>
          <div className="w-1/3 flex items-center gap-2">
            {isProfiling ? (
              <ProfilingIcon width={14} className="fill-teal-500" />
            ) : (
              <LookupIcon width={14} className="fill-teal-500" />
            )}
            <span className="text-[10px]">{getResultText()}</span>
          </div>
        </div>
        <div className="hidden mt-3 w-full gap-2 group-hover:flex">
          {!shouldHideButton && (
            <Button
              variant="outline"
              className="h-8 w-full text-sm font-semibold rounded-xl"
              onClick={action}
              data-testid={`job-item-${data.id}-${label.toLowerCase()}-button`}
            >
              {label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
